/**
 * Teleprompter timing.
 *
 * The prompter scrolls at a speaking pace rather than at an abstract "speed 5": the
 * whole script takes `words ÷ wpm` minutes to read aloud, so it should take that long to
 * scroll past the reading line. Speed in pixels per second is therefore the scroll
 * distance over the speaking time — which also means a bigger font scrolls faster in
 * pixels and exactly as fast in words.
 */

/** Seconds to read `words` aloud at `wordsPerMinute`. */
export function speakingSeconds(words: number, wordsPerMinute: number): number {
  if (words <= 0 || wordsPerMinute <= 0) {
    return 0;
  }
  return (words / wordsPerMinute) * 60;
}

/** Pixels per second that carry `distance` pixels past the reading line in speaking time. */
export function scrollSpeed(
  distance: number,
  words: number,
  wordsPerMinute: number
): number {
  const seconds = speakingSeconds(words, wordsPerMinute);
  if (seconds <= 0 || distance <= 0) {
    return 0;
  }
  return distance / seconds;
}

/** 192 → "3:12"; 3,725 → "1:02:05". */
export function formatClock(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const rest = String(seconds % 60).padStart(2, "0");
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${rest}`;
  }
  return `${minutes}:${rest}`;
}

/** Keep a words-per-minute setting in a sane range. */
export function clampWpm(value: number): number {
  return Math.min(400, Math.max(40, Math.round(value)));
}
